(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-product-series-showcase]';
  var CARD_SELECTOR = '[data-product-series-card]';
  var TAB_SELECTOR = '.collection-tab__tab-item.product-series-vs__tab';
  var SLIDER_SELECTOR = 'slide-section.product-series-vs-swiper';
  var AOS_CARD_SELECTOR = '.psv-card__aos[data-aos]';
  var INTERACTIVE_SELECTOR =
    'a, button, input, textarea, select, label, video, [role="button"], .swiper-arrow, .swiper-button-prev, .swiper-button-next';

  if (window.BoyaProductSeriesShowcase) {
    window.BoyaProductSeriesShowcase.scan(document);
    return;
  }

  var registeredRoots = new WeakMap();
  var originalProductNames = new WeakMap();
  var activeRoots = new Set();
  var mobileQuery = window.matchMedia('(max-width: 767.98px)');
  var nearViewportObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              prepareActivePanel(entry.target);
              nearViewportObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '650px 0px', threshold: 0.01 }
        )
      : null;
  var mobileViewportObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              handleMobileIntersection(entry.target, entry.isIntersecting);
            });
          },
          { threshold: 0 }
        )
      : null;
  var mobilePrewarmObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              handleMobilePrewarm(entry.target, entry.isIntersecting);
            });
          },
          { rootMargin: '260px 0px', threshold: 0 }
        )
      : null;

  function getElement(target) {
    return target && target.nodeType === 1 ? target : target && target.parentElement;
  }

  function getClosest(target, selector) {
    var element = getElement(target);
    return element && element.closest ? element.closest(selector) : null;
  }

  function getCard(root, target) {
    var card = getClosest(target, CARD_SELECTOR);
    return card && root.contains(card) ? card : null;
  }

  function isInteractiveTarget(target) {
    return Boolean(getClosest(target, INTERACTIVE_SELECTOR));
  }

  function protectProductName(title) {
    if (!title || title.nodeType !== 1) return;
    if (!originalProductNames.has(title)) {
      if (!title.matches('.psv-card__title[data-psv-original-title]')) return;
      originalProductNames.set(title, title.getAttribute('data-psv-original-title'));
    }

    var originalName = originalProductNames.get(title);
    if (!title.classList.contains('psv-card__title')) title.classList.add('psv-card__title');
    if (!title.classList.contains('notranslate')) title.classList.add('notranslate');
    if (title.getAttribute('translate') !== 'no') title.setAttribute('translate', 'no');
    if (title.getAttribute('data-transcy') !== 'ignore') title.setAttribute('data-transcy', 'ignore');
    if (!title.hasAttribute('data-no-translation')) title.setAttribute('data-no-translation', '');
    if (title.getAttribute('data-psv-original-title') !== originalName) {
      title.setAttribute('data-psv-original-title', originalName);
    }
    if (title.textContent !== originalName || title.children.length) {
      title.textContent = originalName;
    }
  }

  function scanProductNames(scope) {
    if (!scope) return;
    if (scope.nodeType === 1) protectProductName(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll('.psv-card__title[data-psv-original-title]').forEach(protectProductName);
    }
  }

  function syncSeriesHeadings(root) {
    var panels = Array.from(root.querySelectorAll('.product-series-vs-panel[data-block-id]'));
    root.querySelectorAll(TAB_SELECTOR).forEach(function (tab) {
      var panel = panels.find(function (item) { return item.dataset.blockId === tab.dataset.blockId; });
      var label = tab.querySelector('.product-series-vs__tab-title');
      var heading = panel && panel.querySelector('.product-series-vs__series-heading');
      if (!label || !heading) return;

      var headingId = tab.getAttribute('data-series-heading-id');
      if (headingId) {
        if (heading.id !== headingId) heading.id = headingId;
        if (panel.getAttribute('aria-labelledby') !== headingId) panel.setAttribute('aria-labelledby', headingId);
      }
      // The translated tab label supplies the group name; association always uses the fixed block ID.
      var translatedLabel = label.textContent;
      if (heading.textContent !== translatedLabel || heading.children.length) {
        heading.textContent = translatedLabel;
      }
    });
  }

  function observeProductNames(root, cleanups) {
    scanProductNames(root);
    syncSeriesHeadings(root);
    if (!('MutationObserver' in window)) return;

    // Observe only this showcase; do not freeze translated series labels or descriptions.
    var observer = new MutationObserver(function (mutations) {
      var seriesLabelsChanged = false;
      mutations.forEach(function (mutation) {
        var target = getElement(mutation.target);
        var title = target && originalProductNames.has(target)
          ? target
          : getClosest(target, '.psv-card__title[data-psv-original-title]');
        if (title && root.contains(title)) protectProductName(title);
        if (getClosest(target, TAB_SELECTOR + ', .product-series-vs__series-heading')
          || (target && target.matches('.product-series-vs-panel'))) {
          seriesLabelsChanged = true;
        }
        mutation.addedNodes.forEach(function (node) {
          if (!root.contains(node)) return;
          scanProductNames(node);
          if (node.nodeType === 1 && (
            node.matches(TAB_SELECTOR + ', .product-series-vs__series-heading, .product-series-vs-panel')
            || node.querySelector(TAB_SELECTOR + ', .product-series-vs__series-heading')
          )) seriesLabelsChanged = true;
        });
      });
      if (seriesLabelsChanged) syncSeriesHeadings(root);
    });
    observer.observe(root, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'translate', 'data-transcy', 'data-no-translation', 'data-psv-original-title', 'id', 'aria-labelledby']
    });
    cleanups.push(function () { observer.disconnect(); });
  }

  function hydrateImage(image) {
    if (!image || (!image.hasAttribute('data-src') && !image.hasAttribute('data-srcset'))) return;

    var picture = image.closest('picture');
    if (picture) {
      picture.querySelectorAll('source[data-srcset]').forEach(function (source) {
        source.setAttribute('srcset', source.getAttribute('data-srcset'));
        source.removeAttribute('data-srcset');
      });
    }
    if (image.hasAttribute('data-srcset')) {
      image.setAttribute('srcset', image.getAttribute('data-srcset'));
      image.removeAttribute('data-srcset');
    }
    if (image.hasAttribute('data-src')) {
      image.setAttribute('src', image.getAttribute('data-src'));
      image.removeAttribute('data-src');
    }
    image.setAttribute('data-deferred-image', 'false');
  }

  function preparePanel(panel) {
    if (!panel) return;
    var root = panel.closest(ROOT_SELECTOR);
    if (root) syncSliderLayout(root, panel);
    if (panel.dataset.psvMediaPrepared === '1') return;
    panel.dataset.psvMediaPrepared = '1';
    var mobileList = isMobileList(root);

    panel.querySelectorAll('.psv-card__cover-img--primary').forEach(function (image, index) {
      hydrateImage(image);
      image.loading = !mobileList || index < 4 ? 'eager' : 'lazy';
      image.fetchPriority = index < (mobileList ? 2 : 4) ? 'high' : 'low';
    });

    panel.querySelectorAll('.psv-card__hover-img').forEach(function (image) {
      image.loading = 'lazy';
      image.fetchPriority = 'low';
    });

  }

  function prepareActivePanel(root) {
    var panel = root.querySelector('.collection-tab__tab-content.active');
    preparePanel(panel || root);
  }

  function replayPanelAos(panel) {
    if (!panel || !window.AOS || typeof window.AOS.refreshHard !== 'function') return;
    var cards = panel.querySelectorAll(AOS_CARD_SELECTOR);
    if (!cards.length) return;

    cards.forEach(function (card) {
      card.classList.remove('aos-animate');
    });

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        window.AOS.refreshHard();
      });
    });
  }

  function showHoverImage(card, image) {
    if (card.dataset.psvImageIntent !== '1' || !image.naturalWidth) return;
    window.requestAnimationFrame(function () {
      if (card.dataset.psvImageIntent === '1') {
        card.classList.add('is-image-active');
      }
    });
  }

  function activateImage(card, image) {
    card.dataset.psvImageIntent = '1';
    hydrateImage(image);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    if (image.complete) {
      showHoverImage(card, image);
      return;
    }

    if (card.dataset.psvImagePending === '1') return;
    card.dataset.psvImagePending = '1';

    var ready = image.decode
      ? image.decode()
      : new Promise(function (resolve, reject) {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', reject, { once: true });
        });

    ready
      .then(function () {
        card.dataset.psvImagePending = '0';
        showHoverImage(card, image);
      })
      .catch(function () {
        card.dataset.psvImagePending = '0';
      });
  }

  function isMobile() {
    return mobileQuery.matches;
  }

  function isMobileList(root) {
    return Boolean(root && root.dataset.mobileLayout === 'list' && isMobile());
  }

  function destroySlider(slider) {
    var swiper = getSliderInstance(slider);
    if (swiper && typeof swiper.destroy === 'function') {
      swiper.destroy(true, true);
    }
    slider.globalSlide = null;
    if (slider.swiper && slider.swiper.destroyed) slider.swiper = null;
  }

  function initializeSlider(slider) {
    if (!slider || getSliderInstance(slider) || typeof slider.initSlide !== 'function') return;
    slider.initSlide();
  }

  function syncSliderLayout(root, scope) {
    if (!root) return;
    var sliders = (scope || root).querySelectorAll(SLIDER_SELECTOR);
    sliders.forEach(function (slider) {
      if (isMobileList(root)) {
        destroySlider(slider);
      } else {
        initializeSlider(slider);
      }
    });
  }

  function getMobileVideoSource(video) {
    var sources = Array.from(video.querySelectorAll('source[data-src]'));
    if (!sources.length) return null;

    var mp4Sources = sources.filter(function (source) {
      return (source.getAttribute('type') || '').toLowerCase().indexOf('mp4') !== -1;
    });
    var candidates = mp4Sources.length ? mp4Sources : sources;
    var measured = candidates.filter(function (source) {
      return Number(source.dataset.width) > 0;
    });
    if (!measured.length) return candidates[0];

    var mobileSized = measured.filter(function (source) {
      return Number(source.dataset.width) <= 960;
    });
    if (mobileSized.length) {
      mobileSized.sort(function (a, b) {
        return Number(b.dataset.width) - Number(a.dataset.width);
      });
      return mobileSized[0];
    }

    measured.sort(function (a, b) {
      return Number(a.dataset.width) - Number(b.dataset.width);
    });
    return measured[0];
  }

  function hydrateVideo(video, useMobileSource) {
    var changed = false;
    var selectedSource = useMobileSource ? getMobileVideoSource(video) : null;
    video.querySelectorAll('source[data-src]').forEach(function (source) {
      if (selectedSource && source !== selectedSource) {
        if (source.hasAttribute('src')) {
          source.removeAttribute('src');
          changed = true;
        }
        return;
      }
      if (source.hasAttribute('src') || !source.dataset.src) return;
      source.setAttribute('src', source.dataset.src);
      changed = true;
    });

    if (video.dataset.src && !video.hasAttribute('src')) {
      video.setAttribute('src', video.dataset.src);
      changed = true;
    }

    if (video.dataset.psvPoster && !video.hasAttribute('poster')) {
      video.setAttribute('poster', video.dataset.psvPoster);
    }

    var hydrateMode = useMobileSource ? 'mobile' : 'desktop';
    if (changed || video.dataset.psvHydrated !== hydrateMode) {
      video.dataset.psvHydrated = hydrateMode;
      video.load();
    }
  }

  function configureVideo(video, shouldLoop) {
    video.autoplay = false;
    video.controls = false;
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;
    video.loop = shouldLoop;
    video.preload = 'auto';
    if (shouldLoop) {
      video.setAttribute('loop', '');
    } else {
      video.removeAttribute('loop');
    }
  }

  function revealVideoWhenReady(card, video, intent) {
    function reveal() {
      if (card.dataset.psvVideoIntent === intent) {
        card.classList.add('is-media-active');
      }
    }

    video.onloadeddata = reveal;
    video.oncanplay = reveal;
    if (video.readyState >= 2) reveal();
  }

  function stopVideoCard(card, unload) {
    var video = card.querySelector('.psv-card__video-el');
    card.dataset.psvVideoIntent = '0';
    card.classList.remove('is-media-active');
    if (!video) return;

    var hadRuntimeResource =
      (video.dataset.psvHydrated && video.dataset.psvHydrated !== '0') ||
      video.hasAttribute('src') ||
      video.hasAttribute('poster') ||
      Boolean(video.querySelector('source[src]'));

    video.onloadeddata = null;
    video.oncanplay = null;
    video.onended = null;
    video.pause();
    try {
      video.currentTime = 0;
    } catch (error) {}

    if (!unload) return;

    video.removeAttribute('src');
    video.removeAttribute('poster');
    video.querySelectorAll('source').forEach(function (source) {
      source.removeAttribute('src');
    });
    video.dataset.psvHydrated = '0';
    video.loop = false;
    video.preload = 'none';
    if (hadRuntimeResource) video.load();
  }

  function stopAllMobileVideos(root) {
    root.querySelectorAll(CARD_SELECTOR + ' .psv-card__video-el').forEach(function (video) {
      var card = video.closest(CARD_SELECTOR);
      if (card) stopVideoCard(card, true);
    });
  }

  function playVideo(card, video, intent, onRejected) {
    card.dataset.psvVideoIntent = intent;
    revealVideoWhenReady(card, video, intent);
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {
        if (card.dataset.psvVideoIntent === intent) onRejected();
      });
    }
  }

  function stopPrewarmedVideos(root) {
    root.querySelectorAll(CARD_SELECTOR + '[data-psv-video-intent="prewarm"]').forEach(function (card) {
      stopVideoCard(card, true);
    });
  }

  function prewarmMobileVideo(root, card) {
    if (!card || !isMobile()) return;
    var video = card.querySelector('.psv-card__video-el');
    if (!video || card.dataset.psvVideoIntent === 'mobile') return;

    activeRoots.forEach(function (otherRoot) {
      if (otherRoot !== root) stopPrewarmedVideos(otherRoot);
    });
    root.querySelectorAll(CARD_SELECTOR + ' .psv-card__video-el').forEach(function (otherVideo) {
      var otherCard = otherVideo.closest(CARD_SELECTOR);
      if (otherCard && otherCard !== card) stopVideoCard(otherCard, true);
    });

    configureVideo(video, false);
    hydrateVideo(video, true);
    card.dataset.psvVideoIntent = 'prewarm';
  }

  function startMobileVideo(root, card) {
    activeRoots.forEach(function (otherRoot) {
      if (otherRoot !== root) stopAllMobileVideos(otherRoot);
    });
    root.querySelectorAll(CARD_SELECTOR + ' .psv-card__video-el').forEach(function (otherVideo) {
      var otherCard = otherVideo.closest(CARD_SELECTOR);
      if (!otherCard || (otherCard === card && otherCard.dataset.psvVideoIntent === 'prewarm')) return;
      stopVideoCard(otherCard, true);
    });
    if (!card) return;

    var video = card.querySelector('.psv-card__video-el');
    if (!video) return;

    configureVideo(video, false);
    hydrateVideo(video, true);
    video.onended = function () {
      if (card.dataset.psvVideoIntent === 'mobile') {
        stopVideoCard(card, true);
      }
    };
    playVideo(card, video, 'mobile', function () {
      stopVideoCard(card, true);
    });
  }

  function activateDesktopVideo(card, video) {
    configureVideo(video, true);
    hydrateVideo(video, false);
    playVideo(card, video, 'desktop', function () {
      stopVideoCard(card, false);
    });
  }

  function activateCard(card) {
    if (isMobile()) return;

    var video = card.querySelector('.psv-card__video-el');
    if (video) {
      activateDesktopVideo(card, video);
      return;
    }

    var hoverImage = card.querySelector('.psv-card__hover-img');
    if (hoverImage) activateImage(card, hoverImage);
  }

  function deactivateCard(card) {
    if (isMobile()) return;
    card.dataset.psvImageIntent = '0';
    card.classList.remove('is-image-active');

    if (card.querySelector('.psv-card__video-el')) {
      stopVideoCard(card, false);
    }
  }

  function scrollTabIntoView(tab) {
    var tabsWrap = tab && tab.closest('.product-series-vs__tabs');
    if (!tabsWrap) return;

    if (isMobile()) {
      var mobileTabs = Array.from(tabsWrap.querySelectorAll(TAB_SELECTOR));
      var tabIndex = mobileTabs.indexOf(tab);
      if (tabIndex === -1) return;

      var maxScroll = Math.max(0, tabsWrap.scrollWidth - tabsWrap.clientWidth);
      var targetScroll;
      if (tabIndex === 0) {
        targetScroll = 0;
      } else if (tabIndex === mobileTabs.length - 1) {
        targetScroll = maxScroll;
      } else {
        var mobileWrapRect = tabsWrap.getBoundingClientRect();
        var mobileTabRect = tab.getBoundingClientRect();
        targetScroll =
          tabsWrap.scrollLeft +
          mobileTabRect.left +
          mobileTabRect.width / 2 -
          mobileWrapRect.left -
          mobileWrapRect.width / 2;
      }

      targetScroll = Math.max(0, Math.min(maxScroll, targetScroll));
      tabsWrap.scrollTo({ left: targetScroll, behavior: 'smooth' });
      return;
    }

    var padding = 8;
    var wrapRect = tabsWrap.getBoundingClientRect();
    var tabRect = tab.getBoundingClientRect();
    var targetScroll = tabsWrap.scrollLeft;

    if (tabRect.left < wrapRect.left + padding) {
      targetScroll -= wrapRect.left + padding - tabRect.left;
    } else if (tabRect.right > wrapRect.right - padding) {
      targetScroll += tabRect.right - wrapRect.right + padding;
    } else {
      return;
    }

    tabsWrap.scrollTo({ left: targetScroll, behavior: 'smooth' });
  }

  function getSliderInstance(slider) {
    var swiper = slider && (slider.globalSlide || slider.swiper);
    return swiper && !swiper.destroyed ? swiper : null;
  }

  function getFirstCard(panel) {
    return panel && panel.querySelector('.swiper-slide ' + CARD_SELECTOR);
  }

  function getActiveCard(slider) {
    var swiper = getSliderInstance(slider);
    var activeSlide = swiper && swiper.slides && swiper.slides[swiper.activeIndex];
    if (!activeSlide) activeSlide = slider.querySelector('.swiper-slide-active');
    return (activeSlide && activeSlide.querySelector(CARD_SELECTOR)) || getFirstCard(slider);
  }

  function handleMobileSlideStart(root, slider) {
    var state = registeredRoots.get(root);
    var panel = slider.closest('.collection-tab__tab-content');
    if (!state || !state.inViewport || !isMobile() || isMobileList(root) || !panel || !panel.classList.contains('active')) return;
    stopAllMobileVideos(root);
  }

  function handleMobileSlideEnd(root, slider) {
    var state = registeredRoots.get(root);
    var panel = slider.closest('.collection-tab__tab-content');
    if (!state || !state.inViewport || !isMobile() || isMobileList(root) || !panel || !panel.classList.contains('active')) return;
    startMobileVideo(root, getActiveCard(slider));
  }

  function bindSlider(root, slider, state) {
    if (!slider || isMobileList(root) || state.sliderBindings.has(slider)) return;

    var binding = {
      observer: null,
      swiper: null,
      onStart: null,
      onEnd: null
    };
    state.sliderBindings.set(slider, binding);

    function attach() {
      var swiper = getSliderInstance(slider);
      if (!swiper || typeof swiper.on !== 'function' || binding.swiper === swiper) return;

      binding.swiper = swiper;
      binding.onStart = function () {
        handleMobileSlideStart(root, slider);
      };
      binding.onEnd = function () {
        handleMobileSlideEnd(root, slider);
      };
      swiper.on('slideChangeTransitionStart', binding.onStart);
      swiper.on('slideChangeTransitionEnd', binding.onEnd);
      if (binding.observer) {
        binding.observer.disconnect();
        binding.observer = null;
      }
    }

    attach();
    if (!binding.swiper && 'MutationObserver' in window) {
      binding.observer = new MutationObserver(attach);
      binding.observer.observe(slider, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function resetAndPlayFirstMobileCard(root, panel) {
    if (!panel) return;
    var state = registeredRoots.get(root);
    var slider = panel.querySelector(SLIDER_SELECTOR);
    var mobileList = isMobileList(root);
    if (state && slider && !mobileList) bindSlider(root, slider, state);

    var swiper = getSliderInstance(slider);
    if (!mobileList && swiper && typeof swiper.slideTo === 'function') {
      swiper.slideTo(0, 0, false);
    }
    startMobileVideo(root, getFirstCard(panel));
  }

  function handleMobilePrewarm(root, isInRange) {
    var state = registeredRoots.get(root);
    if (!state) return;
    state.prewarmInRange = isInRange;
    if (!isMobile() || state.inViewport) return;

    if (!isInRange) {
      stopPrewarmedVideos(root);
      return;
    }

    var panel = root.querySelector('.collection-tab__tab-content.active');
    prewarmMobileVideo(root, getFirstCard(panel || root));
  }

  function handleMobileIntersection(root, isIntersecting) {
    var state = registeredRoots.get(root);
    if (!state) return;
    var wasIntersecting = state.inViewport;
    state.inViewport = isIntersecting;
    if (!isMobile() || wasIntersecting === isIntersecting) return;

    if (!isIntersecting) {
      stopAllMobileVideos(root);
      return;
    }

    var panel = root.querySelector('.collection-tab__tab-content.active');
    preparePanel(panel || root);
    resetAndPlayFirstMobileCard(root, panel || root);
  }

  function isRootInViewport(root) {
    var rect = root.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function isRootNearViewport(root, margin) {
    var rect = root.getBoundingClientRect();
    return rect.bottom > -margin && rect.top < window.innerHeight + margin;
  }

  function handleViewportModeChange() {
    activeRoots.forEach(function (root) {
      var state = registeredRoots.get(root);
      if (!state) return;
      var activePanel = root.querySelector('.collection-tab__tab-content.active');
      syncSliderLayout(root, isMobile() ? null : activePanel || root);
      if (!isMobile()) {
        stopAllMobileVideos(root);
        return;
      }

      var activeTab = root.querySelector('.product-series-vs__tab.active');
      if (activeTab) scrollTabIntoView(activeTab);
      state.inViewport = isRootInViewport(root);
      state.prewarmInRange = isRootNearViewport(root, 260);
      if (state.inViewport) {
        var panel = activePanel;
        resetAndPlayFirstMobileCard(root, panel || root);
      } else if (state.prewarmInRange) {
        var nearPanel = activePanel;
        prewarmMobileVideo(root, getFirstCard(nearPanel || root));
      }
    });
  }

  function handleTab(root, tab) {
    var blockId = tab.dataset.blockId;
    if (isMobile()) stopAllMobileVideos(root);
    window.requestAnimationFrame(function () {
      scrollTabIntoView(tab);
      var panel = root.querySelector(
        '.collection-tab__tab-content[data-block-id="' + blockId + '"]'
      );
      preparePanel(panel);

      var slider = panel && panel.querySelector(SLIDER_SELECTOR);
      var swiper = getSliderInstance(slider);
      if (swiper && typeof swiper.updateSize === 'function') {
        swiper.updateSize();
      }

      var state = registeredRoots.get(root);
      if (state && slider && isMobile() && !isMobileList(root)) bindSlider(root, slider, state);
      if (state && state.inViewport && isMobile()) {
        resetAndPlayFirstMobileCard(root, panel);
      } else if (state && state.prewarmInRange && isMobile()) {
        prewarmMobileVideo(root, getFirstCard(panel));
      }
      replayPanelAos(panel);
    });
  }

  function register(root) {
    if (!root || registeredRoots.has(root)) return;

    var cleanups = [];
    var state = {
      cleanups: cleanups,
      inViewport: false,
      prewarmInRange: false,
      sliderBindings: new Map()
    };
    registeredRoots.set(root, state);
    activeRoots.add(root);
    observeProductNames(root, cleanups);
    var initialPanel = root.querySelector('.collection-tab__tab-content.active');
    syncSliderLayout(root, initialPanel || root);
    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      window.customElements.whenDefined('slide-section').then(function () {
        if (!registeredRoots.has(root) || !root.isConnected) return;
        var currentPanel = root.querySelector('.collection-tab__tab-content.active');
        syncSliderLayout(root, currentPanel || root);
      });
    }

    function listen(type, handler) {
      root.addEventListener(type, handler);
      cleanups.push(function () {
        root.removeEventListener(type, handler);
      });
    }

    listen('click', function (event) {
      var tab = getClosest(event.target, TAB_SELECTOR);
      if (tab && root.contains(tab)) handleTab(root, tab);

      var card = getCard(root, event.target);
      if (!card || isInteractiveTarget(event.target)) return;
      var cardLink = card.getAttribute('data-card-url');
      if (cardLink && cardLink !== '#') window.location.href = cardLink;
    });

    listen('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      var card = getCard(root, event.target);
      if (!card || isInteractiveTarget(event.target)) return;
      var cardLink = card.getAttribute('data-card-url');
      if (!cardLink || cardLink === '#') return;
      event.preventDefault();
      window.location.href = cardLink;
    });

    listen('pointerover', function (event) {
      if (event.pointerType === 'touch') return;
      var card = getCard(root, event.target);
      if (!card || (event.relatedTarget && card.contains(event.relatedTarget))) return;
      activateCard(card);
    });

    listen('pointerout', function (event) {
      if (event.pointerType === 'touch') return;
      var card = getCard(root, event.target);
      if (!card || (event.relatedTarget && card.contains(event.relatedTarget))) return;
      deactivateCard(card);
    });

    listen('focusin', function (event) {
      var card = getCard(root, event.target);
      if (card) activateCard(card);
    });

    listen('focusout', function (event) {
      var card = getCard(root, event.target);
      if (!card || (event.relatedTarget && card.contains(event.relatedTarget))) return;
      deactivateCard(card);
    });

    root.dataset.psvEnhanced = '1';
    var activeTab = root.querySelector('.product-series-vs__tab.active');
    if (activeTab) {
      window.requestAnimationFrame(function () {
        scrollTabIntoView(activeTab);
      });
    }

    if (nearViewportObserver) {
      nearViewportObserver.observe(root);
    } else {
      prepareActivePanel(root);
    }

    if (mobilePrewarmObserver) {
      mobilePrewarmObserver.observe(root);
    }
    if (mobileViewportObserver) {
      mobileViewportObserver.observe(root);
    } else {
      handleMobileIntersection(root, isRootInViewport(root));
    }
  }

  function destroy(root) {
    var state = registeredRoots.get(root);
    if (!state) return;
    stopAllMobileVideos(root);
    state.cleanups.forEach(function (cleanup) {
      cleanup();
    });
    state.sliderBindings.forEach(function (binding) {
      if (binding.observer) binding.observer.disconnect();
      if (binding.swiper && typeof binding.swiper.off === 'function') {
        binding.swiper.off('slideChangeTransitionStart', binding.onStart);
        binding.swiper.off('slideChangeTransitionEnd', binding.onEnd);
      }
    });
    registeredRoots.delete(root);
    activeRoots.delete(root);
    delete root.dataset.psvEnhanced;
    if (nearViewportObserver) nearViewportObserver.unobserve(root);
    if (mobilePrewarmObserver) mobilePrewarmObserver.unobserve(root);
    if (mobileViewportObserver) mobileViewportObserver.unobserve(root);
  }

  function scan(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) register(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT_SELECTOR).forEach(register);
  }

  function destroyWithin(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) destroy(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT_SELECTOR).forEach(destroy);
  }

  window.BoyaProductSeriesShowcase = {
    scan: scan,
    destroyWithin: destroyWithin
  };

  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', handleViewportModeChange);
  } else if (typeof mobileQuery.addListener === 'function') {
    mobileQuery.addListener(handleViewportModeChange);
  }

  scan(document);
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyWithin(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    if (!isMobile()) return;
    var root = getClosest(event.target, ROOT_SELECTOR);
    if (!root) return;
    var tab = getClosest(event.target, TAB_SELECTOR);
    if (!tab && event.detail && event.detail.blockId) {
      tab = Array.from(root.querySelectorAll(TAB_SELECTOR)).find(function (item) {
        return item.dataset.blockId === event.detail.blockId;
      });
    }
    if (tab) {
      window.requestAnimationFrame(function () {
        scrollTabIntoView(tab);
      });
    }
  });
})();
